// City catalogue. Each entry powers a /city/:slug landing page — rendering,
// SSR meta, and sitemap. The /city index page (CityIndexPage.tsx) iterates
// this list grouped by region.
//
// 40-city set: all 28 Indian state capitals plus 12 strategic commercial
// cities. Copy is intentionally city-unique (regional character, buyer
// profile, cultural context) to avoid the doorway-page trap that Google
// penalises thin templated city pages for.
//
// Delivery ETA, free-shipping threshold, and COD eligibility are uniform
// pan-India and live in CityPage.tsx, not in the catalogue. This file
// contains only city-unique copy.

export interface CityFaq {
  q: string;
  a: string;
}

export interface CityInfo {
  /** URL slug used in /city/:slug. Lowercase. */
  slug: string;
  /** Display name e.g. "Delhi". */
  name: string;
  /** State / region label e.g. "Delhi NCR", "Maharashtra". */
  state: string;
  /** Broad regional bucket — used for grouping on /city index page. */
  region:
    | "North India"
    | "South India"
    | "West India"
    | "East India"
    | "Central India"
    | "Northeast India";
  /** True if this city is a state or UT capital. Drives the "State Capital" badge. */
  isStateCapital: boolean;
  seoTitle: string;
  seoDescription: string;
  /** ~100–120 word intro under the H1. City-character-driven. */
  intro: string;
  /** 3 FAQs. Each must mention the city by name in both q and a. */
  faqs: CityFaq[];
}
export const cities: CityInfo[] = [];
// export const cities: CityInfo[] = [
//   // ─────────────────────────── ORIGINAL 15 TIER-1 ───────────────────────────
//   {
//     slug: "delhi",
//     name: "Delhi",
//     state: "Delhi NCR",
//     region: "North India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Delhi | ZYN, VELO, Pablo, White Fox | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Delhi NCR — authentic ZYN, VELO, Pablo and White Fox brands with fast pan-India shipping. Cash on Delivery available.",
//     intro:
//       "Delhi NCR — India's National Capital Region — is one of our highest-volume delivery regions. The country's political and commercial nerve centre runs at a relentless pace, and the buyer mix here reflects that: mid-strength mint pouches and premium brands like ZYN and White Fox are consistently the most ordered. Delhi's corporate and government professional crowd makes up the bulk of regular customers from the region. Our shipping network covers Delhi and the wider National Capital Region under the standard pan-India timeline. Whether you are new to nicotine pouches or restocking your regular brand, ZYN Cool Mint and VELO Freezing Peppermint are reliable first picks for buyers in Delhi.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Delhi and the NCR region?",
//         a: "Yes — Delhi and the wider National Capital Region are part of our standard pan-India delivery network. Standard delivery to Delhi addresses lands in 2–4 business days. Next-day express delivery is available for an additional fee.",
//       },
//       {
//         q: "Is Cash on Delivery available for nicotine pouch orders to Delhi?",
//         a: "Yes — Cash on Delivery is available on most Delhi orders. Free shipping kicks in on orders above ₹5,000; below that a flat shipping fee applies at checkout. UPI and major cards are also accepted.",
//       },
//       {
//         q: "Which nicotine pouches are most popular among Delhi buyers?",
//         a: "Delhi orders skew toward mid-strength mints and premium brands. ZYN Cool Mint, VELO Freezing Peppermint and White Fox Slim are the most consistent picks for Delhi NCR buyers.",
//       },
//     ],
//   },

//   {
//     slug: "mumbai",
//     name: "Mumbai",
//     state: "Maharashtra",
//     region: "West India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Mumbai | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Mumbai — slim-format mint and premium brands delivered across the city and Maharashtra. Fast pan-India shipping with COD.",
//     intro:
//       "Mumbai — India's financial capital and most densely populated city — moves at a pace that shapes how people use nicotine pouches. The buyer profile here leans heavily toward slim-format mid-strength pouches that fit into meeting schedules, packed local-train commutes, and back-to-back calls. Mumbai's corporate, finance and media professionals make up the steady customer base, with VELO Slim, ZYN Slim and the lighter Pablo variants seeing the most consistent orders. We deliver across the city, the wider Mumbai Metropolitan Region, and onward through coastal Maharashtra. The free-shipping threshold of ₹5,000 is hit often by Mumbai buyers — bulk orders are common from the city's busier professional cohort.",
//     faqs: [
//       {
//         q: "Do you ship nicotine pouches to Mumbai and the surrounding region?",
//         a: "Yes — Mumbai and the wider Mumbai Metropolitan Region are covered under our standard pan-India delivery network. 2–4 business days standard, with express next-day delivery available for Mumbai pincodes at an additional fee.",
//       },
//       {
//         q: "Does the Mumbai monsoon affect nicotine pouch delivery times?",
//         a: "Heavy monsoon spells in July and August can add 1–2 days to delivery in low-lying parts of Mumbai. Standard delivery is rarely delayed beyond that. We monitor the season and update express-delivery ETAs to Mumbai accordingly.",
//       },
//       {
//         q: "What kind of nicotine pouches do Mumbai buyers usually order?",
//         a: "The Mumbai order mix skews to slim-format mid-strength pouches that suit the city's professional pace. VELO Polar Mint Slim, ZYN Cool Mint and the lighter Pablo Ice Cold variants see the most consistent demand from Mumbai buyers.",
//       },
//     ],
//   },

//   {
//     slug: "bangalore",
//     name: "Bangalore",
//     state: "Karnataka",
//     region: "South India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Bangalore | ZYN, VELO, Pablo | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Bangalore — popular among the city's tech professionals. Authentic ZYN, VELO, Pablo and White Fox with fast pan-India shipping.",
//     intro:
//       "Bangalore — the Silicon Valley of India and Karnataka's tech capital — is consistently one of our top-three cities by order volume. The buyer profile here is younger, more flavour-experimental, and more brand-curious than other Indian metros. Bangalore's IT professional crowd has driven steady demand for non-mint variants — berry, citrus and tropical pouches sell at a noticeably higher rate here than in Delhi or Mumbai. We deliver across the city and the wider Karnataka tech corridor under the standard pan-India shipping timeline. Saturday delivery is common in Bangalore through our courier partners, and the city's weekend ordering pattern shows up clearly in our weekly volumes.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches across Bangalore?",
//         a: "Yes — Bangalore is one of our highest-volume delivery cities. Standard pan-India delivery lands in 2–4 business days for Bangalore pincodes, with next-day express delivery available for an additional fee.",
//       },
//       {
//         q: "Are weekend nicotine pouch deliveries available in Bangalore?",
//         a: "Yes — Saturday delivery is standard across Bangalore through our courier partners. Sunday delivery is available in central parts of Bangalore through our express-delivery partner at an extra fee.",
//       },
//       {
//         q: "Which nicotine pouch flavours do Bangalore buyers usually go for?",
//         a: "Bangalore buyers tend to experiment more with non-mint flavours than other Indian metros. Popular picks in Bangalore include VELO Icy Berries, Pablo Tropical Punch and ZYN Citrus alongside the standard mint lineup.",
//       },
//     ],
//   },

//   {
//     slug: "hyderabad",
//     name: "Hyderabad",
//     state: "Telangana",
//     region: "South India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Hyderabad | ZYN, VELO, Pablo | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Hyderabad — authentic ZYN, VELO, Pablo and White Fox brands delivered with fast pan-India shipping. COD available on most orders.",
//     intro:
//       "Hyderabad — the capital of Telangana and the heart of Cyberabad — has a buyer mix that splits cleanly between the tech corridor and the city's older heritage commerce zones. We see steady weekday volume from corporate professionals in the Cyberabad tech belt, and consistent premium-brand orders from Hyderabad's affluent residential pockets. The city's biryani-and-Bollywood cultural identity coexists with its rapid IT growth, and the pouch mix reflects both — ZYN and VELO dominate the volume, with Pablo seeing strong premium-segment orders. We deliver across Hyderabad and the wider Telangana region under the standard pan-India timeline.",
//     faqs: [
//       {
//         q: "Do you deliver to Hyderabad and Secunderabad?",
//         a: "Yes — Hyderabad, Secunderabad and the wider Telangana region are part of our standard pan-India delivery network. 2–4 business days standard delivery, with next-day express available for Hyderabad pincodes.",
//       },
//       {
//         q: "Is express next-day nicotine pouch delivery available in Hyderabad?",
//         a: "Yes — next-day express delivery is available for Hyderabad city pincodes at an additional shipping fee. Place the order before 1pm on a weekday for next-business-day delivery in Hyderabad.",
//       },
//       {
//         q: "Which brands are most popular among Hyderabad buyers?",
//         a: "ZYN Cool Mint and VELO Polar Mint see strong demand in Hyderabad. Pablo is popular among premium-segment buyers in Hyderabad, and White Fox Slim has steady repeat orders from the city's tech corridor.",
//       },
//     ],
//   },

//   {
//     slug: "chennai",
//     name: "Chennai",
//     state: "Tamil Nadu",
//     region: "South India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Chennai | ZYN, VELO, Pablo | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Chennai — popular among the city's IT and manufacturing workforce. Fast pan-India shipping, COD available on most orders.",
//     intro:
//       "Chennai — Tamil Nadu's capital and South India's manufacturing and IT hub — has a distinct buyer profile that leans practical and conservative. The city's IT corridor and large engineering workforce drive most of our regular orders from Chennai, with slim-format mid-strength pouches making up the majority of repeat purchases. VELO and ZYN slim variants are the popular choices in Chennai, with Pablo picking up steady volume from the stronger-pouch crowd. We deliver across Chennai and the wider Tamil Nadu region under the standard pan-India shipping timeline. Monsoon delivery in October–December may add 1–2 days during particularly heavy spells; otherwise Chennai delivery runs to the regular window year-round.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Chennai?",
//         a: "Yes — Chennai and the wider Tamil Nadu region are part of our standard pan-India delivery network. Standard delivery to Chennai pincodes lands in 2–4 business days, with express next-day delivery available at an additional fee.",
//       },
//       {
//         q: "How does the Chennai monsoon affect nicotine pouch delivery?",
//         a: "Heavy rain in October–December can add 1–2 days to delivery in parts of Chennai. Standard delivery to Chennai usually stays within the regular window outside the peak monsoon spells.",
//       },
//       {
//         q: "What do Chennai buyers usually order?",
//         a: "Chennai's IT-heavy buyer profile leans toward slim-format mid-strength pouches. VELO Polar Mint Slim, ZYN Cool Mint and ZYN Mighty Slim are popular choices in Chennai.",
//       },
//     ],
//   },

//   {
//     slug: "kolkata",
//     name: "Kolkata",
//     state: "West Bengal",
//     region: "East India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Kolkata | ZYN, VELO, Pablo | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Kolkata — authentic ZYN, VELO, Pablo and White Fox with fast shipping across eastern India. Cash on Delivery available.",
//     intro:
//       "Kolkata — the cultural and literary capital of eastern India — is our primary eastern hub for nicotine pouch delivery. The city's distinct cultural identity and slower commercial pace show up in the buyer profile: orders skew toward mint and lighter pouches, with ZYN Cool Mint and VELO Freezing Peppermint seeing the most consistent demand from Kolkata buyers. The city's IT and government professional populations make up the regular customer base. We deliver across Kolkata and the wider West Bengal region under the standard pan-India timeline. Kolkata is also a reliable forwarding hub for our wider eastern India deliveries.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Kolkata?",
//         a: "Yes — Kolkata and the wider West Bengal region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Kolkata pincodes, with next-day express available for an additional fee.",
//       },
//       {
//         q: "Is Cash on Delivery available for orders to Kolkata?",
//         a: "Yes — Cash on Delivery is available on most Kolkata orders. Free shipping kicks in on orders over ₹5,000; below that a flat shipping fee applies at checkout. UPI and cards are also accepted.",
//       },
//       {
//         q: "Which nicotine pouches are most ordered from Kolkata?",
//         a: "Kolkata orders lean toward mint and lighter-strength pouches. ZYN Cool Mint, VELO Freezing Peppermint and CLEW Mint are the most consistent picks for Kolkata buyers.",
//       },
//     ],
//   },

//   {
//     slug: "pune",
//     name: "Pune",
//     state: "Maharashtra",
//     region: "West India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Pune | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Pune — popular among the city's student and young-professional crowd. Authentic brands with fast pan-India shipping and COD.",
//     intro:
//       "Pune — Maharashtra's cultural capital and India's Oxford of the East — has a buyer profile that reflects its dual identity as both an education hub and a major IT centre. The city's massive student population and young-professional IT workforce push the order mix toward berry, tropical and citrus pouches alongside the standard mints. Pune buyers are noticeably more flavour-experimental than Mumbai or Delhi customers. We deliver across Pune, the Pimpri-Chinchwad twin city, and the wider Maharashtra region under the standard pan-India timeline. Express next-day delivery is available across most Pune addresses for buyers who need a faster restock.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches across Pune and Pimpri-Chinchwad?",
//         a: "Yes — Pune, Pimpri-Chinchwad and the wider region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Pune pincodes, with next-day express also available.",
//       },
//       {
//         q: "Is express next-day delivery available in Pune?",
//         a: "Yes — express next-day delivery is available for most Pune addresses at an additional shipping fee. Common pattern for Pune buyers who restock around the city's IT-park weekend rhythm.",
//       },
//       {
//         q: "Which pouches do Pune buyers order most?",
//         a: "Pune's younger buyer profile shows up in the order mix — berry, tropical and citrus pouches see strong interest in Pune alongside standard mints. Popular picks include VELO Icy Berries, Pablo Tropical Punch and ZYN Citrus.",
//       },
//     ],
//   },

//   {
//     slug: "ahmedabad",
//     name: "Ahmedabad",
//     state: "Gujarat",
//     region: "West India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Ahmedabad | ZYN, VELO, Pablo | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Ahmedabad — authentic ZYN, VELO, Pablo brands with fast shipping across Gujarat and India. COD widely available.",
//     intro:
//       "Ahmedabad — Gujarat's commercial capital and the Manchester of India — has a steady, brand-loyal buyer profile. The city's trading and manufacturing roots show up in the order mix: ZYN and VELO are clear preferences, with mint-format slim pouches dominating the volume from Ahmedabad. We deliver across Ahmedabad, Gandhinagar (including GIFT City), and the wider Gujarat region under the standard pan-India shipping timeline. Cash on Delivery is well-used by Ahmedabad buyers — the city has one of the higher COD-vs-prepaid ratios in our overall mix. For first-time buyers from Ahmedabad, ZYN Cool Mint Slim and VELO Polar Mint Slim are the safest two cans to start with.",
//     faqs: [
//       {
//         q: "Do you ship nicotine pouches to Ahmedabad and Gandhinagar?",
//         a: "Yes — Ahmedabad, Gandhinagar (including GIFT City) and the wider Gujarat region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Ahmedabad pincodes.",
//       },
//       {
//         q: "Is Cash on Delivery available in Ahmedabad?",
//         a: "Yes — Cash on Delivery is widely used in Ahmedabad and available on most orders. Free shipping kicks in on orders over ₹5,000. UPI and cards are also accepted at checkout.",
//       },
//       {
//         q: "What do Ahmedabad buyers usually order?",
//         a: "Ahmedabad orders lean conservative and brand-loyal. ZYN Cool Mint Slim, VELO Polar Mint Slim and ZYN Mighty Slim are popular choices in Ahmedabad. Mint pouches dominate the mix.",
//       },
//     ],
//   },

//   {
//     slug: "jaipur",
//     name: "Jaipur",
//     state: "Rajasthan",
//     region: "North India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Jaipur | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Jaipur — authentic ZYN, VELO, Pablo brands delivered with fast pan-India shipping across Rajasthan. COD available.",
//     intro:
//       "Jaipur — Rajasthan's capital and the Pink City — is one of our fastest-growing tier-1 markets. The city's mix of heritage commerce, tourism-related demand, and a fast-rising IT and corporate base shapes the buyer profile. ZYN, VELO and Pablo see roughly equal traction in Jaipur, with mint and lighter strengths leading the order mix. We deliver across Jaipur and the wider Rajasthan region under the standard pan-India shipping timeline. Standard delivery to most Jaipur addresses lands within the 2–4 day window, and Cash on Delivery is available across the city's pincodes.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Jaipur?",
//         a: "Yes — Jaipur and the wider Rajasthan region are part of our standard pan-India delivery network. Standard delivery to Jaipur pincodes lands in 2–4 business days. Cash on Delivery is available on most Jaipur orders.",
//       },
//       {
//         q: "Is Cash on Delivery available for orders to Jaipur?",
//         a: "Yes — Cash on Delivery is widely available for Jaipur orders. Free shipping applies to orders above ₹5,000, and UPI and card payments are also accepted at checkout.",
//       },
//       {
//         q: "Which nicotine pouches are popular among Jaipur buyers?",
//         a: "Jaipur's growing market shows balanced demand across brands. ZYN Cool Mint, VELO Freezing Peppermint and Pablo Ice Cold are the most consistent picks for buyers in Jaipur.",
//       },
//     ],
//   },

//   {
//     slug: "lucknow",
//     name: "Lucknow",
//     state: "Uttar Pradesh",
//     region: "North India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Lucknow | ZYN, VELO, Pablo | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Lucknow — authentic ZYN, VELO, Pablo and White Fox with fast pan-India shipping. Cash on Delivery available across the city.",
//     intro:
//       "Lucknow — the city of Nawabs and Uttar Pradesh's capital — combines heritage commerce with a fast-growing service economy. The buyer profile here is brand-loyal and traditionally conservative: ZYN and VELO mint variants dominate the order mix from Lucknow, with stronger pouches like Pablo and White Fox seeing steady but smaller volumes. Lucknow's government, education, and service-sector professionals make up most of our regular customers from the city. We deliver across Lucknow and the wider Uttar Pradesh region under the standard pan-India timeline. For first-time buyers from Lucknow, ZYN Cool Mint at 6mg is the most-ordered starter pouch.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Lucknow?",
//         a: "Yes — Lucknow and the wider Uttar Pradesh region are part of our standard pan-India delivery network. Standard delivery to Lucknow addresses lands in 2–4 business days with COD available on most orders.",
//       },
//       {
//         q: "Is Cash on Delivery available in Lucknow?",
//         a: "Yes — Cash on Delivery is available on most Lucknow orders. Free shipping kicks in on orders over ₹5,000; below that a flat shipping fee applies at checkout.",
//       },
//       {
//         q: "What do first-time buyers in Lucknow usually order?",
//         a: "First-time Lucknow buyers tend to start with ZYN Cool Mint at 6mg or VELO Polar Mint Slim. The Lucknow order mix is mint-heavy and brand-loyal, with steady repeat demand.",
//       },
//     ],
//   },

//   {
//     slug: "chandigarh",
//     name: "Chandigarh",
//     state: "Punjab / Haryana (UT)",
//     region: "North India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Chandigarh | Premium Brands | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Chandigarh — premium ZYN, VELO, White Fox brands with fast shipping across the tricity area. Cash on Delivery available.",
//     intro:
//       "Chandigarh — India's first planned city and the joint capital of Punjab and Haryana — has one of the highest per-capita affluence levels among Indian cities, which shows clearly in the buyer mix. Premium brands like White Fox and the higher-end ZYN variants see disproportionately strong demand from Chandigarh compared to other Indian cities. The tricity area (Chandigarh, Mohali, Panchkula) accounts for steady weekend ordering volume. We deliver across Chandigarh and onward to Punjab and Haryana under the standard pan-India shipping timeline. The city's planned grid layout makes delivery navigation among the most reliable in our network.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches across the Chandigarh tricity area?",
//         a: "Yes — Chandigarh, Mohali and Panchkula are part of our standard pan-India delivery network. 2–4 business days standard delivery to Chandigarh tricity pincodes, with next-day express also available.",
//       },
//       {
//         q: "Which premium nicotine pouches are popular in Chandigarh?",
//         a: "Chandigarh's affluent buyer profile shows strong demand for premium brands. White Fox Slim, ZYN's higher-end variants and Pablo Exclusive are popular choices in Chandigarh.",
//       },
//       {
//         q: "Is Cash on Delivery available in Chandigarh?",
//         a: "Yes — Cash on Delivery is available on most Chandigarh orders. Free shipping kicks in on orders over ₹5,000. UPI and all major cards are also accepted at checkout.",
//       },
//     ],
//   },

//   {
//     slug: "kochi",
//     name: "Kochi",
//     state: "Kerala",
//     region: "South India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Kochi | ZYN, VELO, Pablo | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Kochi — authentic ZYN, VELO, Pablo, White Fox with fast pan-India shipping across Kerala. Cash on Delivery available.",
//     intro:
//       "Kochi — Kerala's commercial capital and the Queen of the Arabian Sea — is our primary delivery hub for the south-western coast. The city's mix of port commerce, tourism, and IT (Infopark) shapes a varied buyer profile, but mint pouches and slim formats lead the order mix from Kochi. Kerala buyers are slightly more conservative in their brand mix — ZYN and VELO dominate, with Pablo picking up niche premium demand. We deliver across Kochi and onward through Kerala under the standard pan-India shipping timeline. Monsoon delays in June–August may add 1–2 days to express delivery to Kochi; standard delivery stays close to the regular window.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Kochi and across Kerala?",
//         a: "Yes — Kochi and the wider Kerala region are part of our standard pan-India delivery network. Standard delivery to Kochi pincodes lands in 2–4 business days, with COD available on most orders.",
//       },
//       {
//         q: "Does the Kerala monsoon affect delivery to Kochi?",
//         a: "Heavy monsoon spells in June–August can add 1–2 days to delivery in parts of Kochi and coastal Kerala. Standard delivery to Kochi usually stays within the regular window outside the peak monsoon period.",
//       },
//       {
//         q: "What do Kochi buyers usually order?",
//         a: "Kochi's order mix leans toward mint and slim-format pouches. ZYN Cool Mint, VELO Polar Mint Slim and the lighter Pablo variants are the most consistent picks for Kochi buyers.",
//       },
//     ],
//   },

//   {
//     slug: "surat",
//     name: "Surat",
//     state: "Gujarat",
//     region: "West India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Surat | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Surat — authentic ZYN, VELO, Pablo brands with fast pan-India shipping across Gujarat. Cash on Delivery widely available.",
//     intro:
//       "Surat — India's diamond capital and the country's textile manufacturing heart — has been one of our fastest-growing tier-1 markets over the past year. The city's commercial and trading-community demographic shows up in the order mix: brand-loyal, mint-heavy, with strong volume on ZYN and VELO slim variants. Surat's fast-growing IT base also drives a smaller but steady flow of orders for berry and tropical pouches. We deliver across Surat and the wider Gujarat coastal belt under the standard pan-India shipping timeline. Cash on Delivery is well-used in Surat — a noticeable share of orders here come through the COD route.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Surat?",
//         a: "Yes — Surat and the wider Gujarat region are part of our standard pan-India delivery network. Standard delivery to Surat pincodes lands in 2–4 business days with COD widely available.",
//       },
//       {
//         q: "Is Cash on Delivery commonly used in Surat?",
//         a: "Yes — Surat has one of the higher COD usage rates in our network. Cash on Delivery is available on most Surat orders, and UPI plus card payments are also accepted at checkout.",
//       },
//       {
//         q: "Which brands do Surat buyers prefer?",
//         a: "Surat's order mix is brand-loyal and mint-heavy. ZYN Cool Mint Slim and VELO Polar Mint Slim are the most consistent picks for Surat buyers, with Pablo seeing steady volume from the city's stronger-pouch crowd.",
//       },
//     ],
//   },

//   {
//     slug: "coimbatore",
//     name: "Coimbatore",
//     state: "Tamil Nadu",
//     region: "South India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Coimbatore | ZYN, VELO | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Coimbatore — authentic ZYN, VELO, Pablo brands with fast pan-India shipping across Tamil Nadu. COD available.",
//     intro:
//       "Coimbatore — Tamil Nadu's second-largest city and the Manchester of South India — has a buyer profile shaped by its engineering, textile, and rapidly growing IT base. The city's industrial and corporate workforce drives the bulk of our regular orders from Coimbatore, with VELO and ZYN slim mints leading the volume. Coimbatore buyers tend to order in steady, smaller quantities — a regular restock rhythm rather than bulk buying. We deliver across Coimbatore and the wider Kongu Nadu region under the standard pan-India shipping timeline. The city is also a reliable forwarding hub for orders heading deeper into the southern interior of Tamil Nadu and into northern Kerala.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Coimbatore?",
//         a: "Yes — Coimbatore and the wider Tamil Nadu region are part of our standard pan-India delivery network. Standard delivery to Coimbatore pincodes lands in 2–4 business days with COD available on most orders.",
//       },
//       {
//         q: "Is Cash on Delivery available in Coimbatore?",
//         a: "Yes — Cash on Delivery is available on most Coimbatore orders. Free shipping kicks in on orders over ₹5,000. UPI and card payments are also accepted at checkout.",
//       },
//       {
//         q: "What do Coimbatore buyers usually order?",
//         a: "Coimbatore's industrial and IT workforce drives steady mint-format orders. VELO Polar Mint Slim and ZYN Cool Mint are the most consistent picks for Coimbatore buyers.",
//       },
//     ],
//   },

//   {
//     slug: "indore",
//     name: "Indore",
//     state: "Madhya Pradesh",
//     region: "Central India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Indore | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Indore — authentic ZYN, VELO, Pablo brands with fast pan-India shipping across Madhya Pradesh. Cash on Delivery available.",
//     intro:
//       "Indore — Madhya Pradesh's commercial capital and consistently ranked India's cleanest city — has been one of the fastest-rising tier-1 markets in our network. The city's pragmatic, value-conscious buyer profile shows up in the order mix: mid-strength pouches dominate, with ZYN Cool Mint and VELO Freezing Peppermint as the most consistent picks. Indore's growing IT and education base, combined with its strong manufacturing and service economies, drives a varied but steady demand. We deliver across Indore and the wider Madhya Pradesh region under the standard pan-India shipping timeline. Cash on Delivery is widely used in Indore for first-time orders.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Indore?",
//         a: "Yes — Indore and the wider Madhya Pradesh region are part of our standard pan-India delivery network. Standard delivery to Indore pincodes lands in 2–4 business days with COD available on most orders.",
//       },
//       {
//         q: "Is Cash on Delivery available in Indore?",
//         a: "Yes — Cash on Delivery is widely used in Indore and available on most orders, especially for first-time buyers. UPI and card payments are also accepted at checkout.",
//       },
//       {
//         q: "Which nicotine pouches are popular among Indore buyers?",
//         a: "Indore's order mix leans toward mid-strength mints. ZYN Cool Mint, VELO Freezing Peppermint and ZYN Mighty Slim are the most consistent picks for Indore buyers.",
//       },
//     ],
//   },

//   // ─────────────────────────── ADDITIONAL 25 CITIES ───────────────────────────
//   // 19 state capitals not in the tier-1 list + 6 strategic commercial cities
//   // (Nagpur, Visakhapatnam, Vadodara, Kanpur, Agra, Varanasi).

//   {
//     slug: "patna",
//     name: "Patna",
//     state: "Bihar",
//     region: "East India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Patna | ZYN, VELO, CLEW | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Patna — authentic ZYN, VELO and CLEW brands with fast pan-India shipping across Bihar. Cash on Delivery widely available.",
//     intro:
//       "Patna — Bihar's capital and one of the oldest continuously inhabited cities in the world — is our delivery hub for the eastern Gangetic plain. The city's history as ancient Pataliputra runs alongside its modern role as Bihar's administrative and educational centre. The buyer profile from Patna leans conservative and brand-loyal, with ZYN and VELO mint variants leading the order mix. We deliver across Patna and the wider Bihar region under the standard pan-India shipping timeline. Cash on Delivery is widely used for first-time Patna orders, and the city is also our forwarding hub for Muzaffarpur, Gaya and the broader Bihar interior.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Patna?",
//         a: "Yes — Patna and the wider Bihar region are part of our standard pan-India delivery network. Standard delivery to Patna pincodes lands in 2–4 business days with COD available on most orders.",
//       },
//       {
//         q: "Is Cash on Delivery available in Patna?",
//         a: "Yes — Cash on Delivery is widely used in Patna and available on most orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "Which nicotine pouches are popular in Patna?",
//         a: "Patna orders lean conservative and brand-loyal. ZYN Cool Mint, VELO Polar Mint Slim and CLEW Mint are the most consistent picks for Patna buyers.",
//       },
//     ],
//   },

//   {
//     slug: "bhopal",
//     name: "Bhopal",
//     state: "Madhya Pradesh",
//     region: "Central India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Bhopal | ZYN, VELO | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Bhopal — Madhya Pradesh's capital. Authentic ZYN, VELO, Pablo brands with fast pan-India shipping and COD.",
//     intro:
//       "Bhopal — Madhya Pradesh's capital and the City of Lakes — combines a heritage-rich old city with a planned new city and a growing administrative-service economy. The buyer profile from Bhopal is steady and mid-strength-leaning, dominated by ZYN and VELO mint variants. The city's professional and government workforce drives most of our regular orders, with a small but steady premium segment from the affluent residential belts. We deliver across Bhopal and the wider Madhya Pradesh region under the standard pan-India timeline. Bhopal also acts as our forwarding hub for Jabalpur and the broader central MP region.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Bhopal?",
//         a: "Yes — Bhopal and the wider Madhya Pradesh region are part of our standard pan-India delivery network. 2–4 business days standard delivery with COD on most Bhopal orders.",
//       },
//       {
//         q: "Is Cash on Delivery available in Bhopal?",
//         a: "Yes — Cash on Delivery is available on most Bhopal orders. Free shipping kicks in on orders over ₹5,000. UPI and card payments are also accepted.",
//       },
//       {
//         q: "Which brands are popular in Bhopal?",
//         a: "Bhopal's order mix leans mid-strength mint. ZYN Cool Mint, VELO Polar Mint Slim and Pablo Ice Cold are the most consistent picks for Bhopal buyers.",
//       },
//     ],
//   },

//   {
//     slug: "bhubaneswar",
//     name: "Bhubaneswar",
//     state: "Odisha",
//     region: "East India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Bhubaneswar | ZYN, VELO | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Bhubaneswar — Odisha's capital and a smart city. Authentic brands with fast pan-India shipping and COD.",
//     intro:
//       "Bhubaneswar — Odisha's capital and the Temple City of India — has transformed from an ancient temple town into one of India's first smart cities. The buyer profile here reflects the city's growing IT and education base alongside its administrative core. ZYN and VELO mint variants lead the order mix from Bhubaneswar, with steady demand for slim-format mid-strength pouches. We deliver across Bhubaneswar, Cuttack, and the wider Odisha region under the standard pan-India shipping timeline. The city is also our forwarding hub for orders heading into the rest of Odisha.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Bhubaneswar?",
//         a: "Yes — Bhubaneswar, Cuttack and the wider Odisha region are part of our standard pan-India delivery network. Standard delivery to Bhubaneswar pincodes lands in 2–4 business days.",
//       },
//       {
//         q: "Is Cash on Delivery available in Bhubaneswar?",
//         a: "Yes — Cash on Delivery is available on most Bhubaneswar orders. UPI and card payments are also accepted at checkout.",
//       },
//       {
//         q: "What do Bhubaneswar buyers usually order?",
//         a: "Bhubaneswar's growing IT base shows up in the order mix. ZYN Cool Mint, VELO Polar Mint Slim and ZYN Mighty Slim are the most consistent picks for Bhubaneswar buyers.",
//       },
//     ],
//   },

//   {
//     slug: "raipur",
//     name: "Raipur",
//     state: "Chhattisgarh",
//     region: "Central India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Raipur | ZYN, VELO | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Raipur — Chhattisgarh's capital. Authentic ZYN and VELO with fast pan-India shipping. Cash on Delivery widely available.",
//     intro:
//       "Raipur — Chhattisgarh's capital and the state's largest commercial centre — sits at the heart of India's steel and rice belt. The city's industrial roots and growing administrative role drive a steady buyer profile, with mid-strength mint pouches making up the bulk of orders from Raipur. We deliver across Raipur and the wider Chhattisgarh region under the standard pan-India timeline. Cash on Delivery is widely used in Raipur, particularly for first-time buyers.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Raipur?",
//         a: "Yes — Raipur and the wider Chhattisgarh region are part of our standard pan-India delivery network. 2–4 business days standard delivery with COD on most Raipur orders.",
//       },
//       {
//         q: "Is Cash on Delivery available in Raipur?",
//         a: "Yes — Cash on Delivery is widely used in Raipur and available on most orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "Which pouches do Raipur buyers prefer?",
//         a: "Raipur's order mix leans mid-strength mint. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Raipur buyers.",
//       },
//     ],
//   },

//   {
//     slug: "ranchi",
//     name: "Ranchi",
//     state: "Jharkhand",
//     region: "East India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Ranchi | ZYN, VELO | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Ranchi — Jharkhand's capital. Authentic ZYN and VELO with fast pan-India shipping across Jharkhand. COD available.",
//     intro:
//       "Ranchi — Jharkhand's capital and the City of Waterfalls — has grown from a tribal-region administrative centre into a fast-rising tier-2 commercial hub. The city's mining-services economy and growing IT and education base shape a varied buyer profile. ZYN and VELO see steady demand from Ranchi, with mid-strength mints leading the mix. We deliver across Ranchi, Jamshedpur, and the wider Jharkhand region under the standard pan-India timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Ranchi?",
//         a: "Yes — Ranchi, Jamshedpur and the wider Jharkhand region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Ranchi pincodes.",
//       },
//       {
//         q: "Is Cash on Delivery available in Ranchi?",
//         a: "Yes — Cash on Delivery is available on most Ranchi orders. UPI and card payments are also accepted at checkout.",
//       },
//       {
//         q: "What do Ranchi buyers usually order?",
//         a: "Ranchi orders lean toward mid-strength mints. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Ranchi buyers.",
//       },
//     ],
//   },

//   {
//     slug: "dehradun",
//     name: "Dehradun",
//     state: "Uttarakhand",
//     region: "North India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Dehradun | ZYN, VELO | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Dehradun — Uttarakhand's capital in the Doon Valley. Authentic brands with fast pan-India shipping and COD.",
//     intro:
//       "Dehradun — Uttarakhand's capital and the gateway to India's hill stations — has a buyer profile shaped by its education hub status and growing residential commerce. The Doon Valley setting attracts a steady flow of military, government, education, and small-business professionals. ZYN and VELO mint variants lead the order mix from Dehradun. We deliver across Dehradun and the wider Uttarakhand region under the standard pan-India shipping timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Dehradun?",
//         a: "Yes — Dehradun and the wider Uttarakhand region are part of our standard pan-India delivery network. Standard delivery to Dehradun pincodes lands in 2–4 business days.",
//       },
//       {
//         q: "Is Cash on Delivery available in Dehradun?",
//         a: "Yes — COD is available on most Dehradun orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "Which brands are popular in Dehradun?",
//         a: "Dehradun's mix shows steady mid-strength mint preference. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Dehradun buyers.",
//       },
//     ],
//   },

//   {
//     slug: "thiruvananthapuram",
//     name: "Thiruvananthapuram",
//     state: "Kerala",
//     region: "South India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Thiruvananthapuram | ZYN, VELO | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Thiruvananthapuram (Trivandrum) — Kerala's capital. Authentic brands with fast pan-India shipping and COD.",
//     intro:
//       "Thiruvananthapuram — Kerala's capital and home to Technopark, India's largest IT park by built-up area — anchors our delivery network in the southern peninsula. The buyer profile here mirrors Kerala's broader pattern: conservative brand mix, mint-heavy, slim-format preference. The IT corridor along Technopark, alongside the city's traditional government and education base, drives steady weekday orders. We deliver across Thiruvananthapuram and onward through southern Kerala under the standard pan-India shipping timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Thiruvananthapuram?",
//         a: "Yes — Thiruvananthapuram (Trivandrum) and the wider southern Kerala region are part of our standard pan-India delivery network. 2–4 business days standard delivery.",
//       },
//       {
//         q: "Does the Kerala monsoon affect delivery to Thiruvananthapuram?",
//         a: "Heavy monsoon spells in June–August can add 1–2 days to delivery in parts of Thiruvananthapuram. Standard delivery usually stays close to the regular window.",
//       },
//       {
//         q: "What do Thiruvananthapuram buyers usually order?",
//         a: "Thiruvananthapuram's IT and government workforce drives steady mint-format orders. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks here.",
//       },
//     ],
//   },

//   {
//     slug: "gandhinagar",
//     name: "Gandhinagar",
//     state: "Gujarat",
//     region: "West India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Gandhinagar | GIFT City Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Gandhinagar — Gujarat's capital and GIFT City. Authentic ZYN, VELO with fast pan-India shipping.",
//     intro:
//       "Gandhinagar — Gujarat's planned capital city and home to GIFT City (India's first International Financial Services Centre) — has a buyer profile shaped by its administrative, financial, and emerging-tech base. The order mix leans toward mid-strength mint pouches, with ZYN and VELO leading. Gandhinagar buyers often combine deliveries with Ahmedabad addresses given the cities' proximity. We deliver across Gandhinagar and the wider Gujarat region under the standard pan-India timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Gandhinagar and GIFT City?",
//         a: "Yes — Gandhinagar and GIFT City are part of our standard pan-India delivery network. 2–4 business days standard delivery to Gandhinagar pincodes.",
//       },
//       {
//         q: "Is Cash on Delivery available in Gandhinagar?",
//         a: "Yes — COD is available on most Gandhinagar orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "Which brands are popular in Gandhinagar?",
//         a: "Gandhinagar's administrative and financial buyer profile shows steady mint preference. ZYN Cool Mint Slim and VELO Polar Mint Slim are the most consistent picks here.",
//       },
//     ],
//   },

//   {
//     slug: "panaji",
//     name: "Panaji",
//     state: "Goa",
//     region: "West India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Panaji, Goa | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Panaji and across Goa — authentic ZYN, VELO, Pablo brands with fast pan-India shipping. COD available.",
//     intro:
//       "Panaji — Goa's capital and a Portuguese-heritage riverside city — anchors our delivery network on India's western coast. Goa's mix of tourism, casino-hospitality, IT, and resident commerce drives a varied buyer profile, but mint and slim-format pouches lead the order mix from Panaji. We deliver across Panaji, Vasco, Margao, and the wider Goa region under the standard pan-India shipping timeline. Monsoon delivery in June–September can add 1–2 days during peak spells; otherwise Goa delivery runs to the regular window year-round.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Panaji and across Goa?",
//         a: "Yes — Panaji, Vasco, Margao and the wider Goa region are part of our standard pan-India delivery network. 2–4 business days standard delivery.",
//       },
//       {
//         q: "Does the Goa monsoon affect delivery to Panaji?",
//         a: "Heavy monsoon spells in June–September can add 1–2 days to delivery across Goa. Standard delivery to Panaji stays close to the regular window outside peak monsoon.",
//       },
//       {
//         q: "What do Panaji buyers usually order?",
//         a: "Panaji's order mix leans toward mint and slim-format pouches. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Panaji buyers.",
//       },
//     ],
//   },

//   {
//     slug: "shimla",
//     name: "Shimla",
//     state: "Himachal Pradesh",
//     region: "North India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Shimla, HP | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Shimla — Himachal Pradesh's capital. Authentic ZYN, VELO with fast pan-India shipping. Cash on Delivery available.",
//     intro:
//       "Shimla — Himachal Pradesh's capital and the former summer capital of British India — sits in the Lower Himalayas with a buyer profile shaped by tourism, government, and education. The city's altitude and weather make for distinct delivery considerations during winter, with December–February occasionally adding 1–2 days during heavy snowfall. ZYN and VELO mint variants lead the order mix from Shimla. We deliver across Shimla and the wider Himachal Pradesh region under the standard pan-India timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Shimla?",
//         a: "Yes — Shimla and the wider Himachal Pradesh region are part of our standard pan-India delivery network. Standard delivery to Shimla pincodes lands in 2–4 business days.",
//       },
//       {
//         q: "Does the Shimla winter affect delivery times?",
//         a: "Heavy snowfall in December–February can add 1–2 days to delivery in Shimla and across upper Himachal Pradesh. Standard delivery usually stays close to the regular window outside peak winter spells.",
//       },
//       {
//         q: "Is Cash on Delivery available in Shimla?",
//         a: "Yes — Cash on Delivery is available on most Shimla orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//     ],
//   },

//   {
//     slug: "guwahati",
//     name: "Guwahati",
//     state: "Assam",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Guwahati, Assam | Northeast Hub | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Guwahati — gateway to northeast India. Authentic ZYN, VELO, Pablo with fast pan-India shipping. COD available.",
//     intro:
//       "Guwahati — Assam's largest city and the gateway to India's northeast — anchors our delivery network for the seven sister states. The city's mix of trade, education, oil and gas, and growing IT drives a steady buyer profile, with ZYN and VELO mint variants leading the order mix. We deliver across Guwahati, Dispur (Assam's administrative capital), and onward through Assam and the wider northeast under the standard pan-India shipping timeline. The city is our reliable forwarding hub for orders heading deeper into the northeastern region.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Guwahati?",
//         a: "Yes — Guwahati, Dispur and the wider Assam region are part of our standard pan-India delivery network. Standard delivery to Guwahati pincodes lands in 2–4 business days.",
//       },
//       {
//         q: "Do you ship beyond Guwahati to the rest of Assam and the northeast?",
//         a: "Yes — Guwahati is our forwarding hub for the wider northeast. Orders to Assam, Meghalaya, Arunachal Pradesh and other northeastern states pass through here under the standard pan-India timeline.",
//       },
//       {
//         q: "What do Guwahati buyers usually order?",
//         a: "Guwahati's diverse buyer base shows steady mint-format preference. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Guwahati buyers.",
//       },
//     ],
//   },

//   {
//     slug: "itanagar",
//     name: "Itanagar",
//     state: "Arunachal Pradesh",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Itanagar, Arunachal Pradesh | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Itanagar — Arunachal Pradesh's capital. Authentic brands with pan-India shipping. COD available.",
//     intro:
//       "Itanagar — Arunachal Pradesh's capital and the gateway to India's easternmost frontier — sits in the foothills of the Himalayas. The buyer profile from Itanagar is modest in volume but steady, mirroring the state's small but growing service-economy base. ZYN and VELO mint variants lead the order mix. We deliver across Itanagar and the wider Arunachal Pradesh region under our pan-India shipping network, with Guwahati acting as the regional forwarding hub. Standard delivery typically takes a day or two longer than the lower-northeast cities, particularly during monsoon.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Itanagar?",
//         a: "Yes — Itanagar and the wider Arunachal Pradesh region are part of our pan-India delivery network. Delivery to Itanagar typically lands in 3–5 business days given the eastern frontier location.",
//       },
//       {
//         q: "Is Cash on Delivery available in Itanagar?",
//         a: "Yes — Cash on Delivery is available on most Itanagar orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Itanagar buyers usually order?",
//         a: "Itanagar's order mix leans toward mint-format pouches. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Itanagar buyers.",
//       },
//     ],
//   },

//   {
//     slug: "imphal",
//     name: "Imphal",
//     state: "Manipur",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Imphal, Manipur | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Imphal — Manipur's capital. Authentic brands delivered with pan-India shipping. COD available.",
//     intro:
//       "Imphal — Manipur's capital and the cultural heart of the northeastern state — has a buyer profile shaped by its administrative, education, and small-trade base. The city sits in a valley surrounded by hills, and our delivery network reaches Imphal through Guwahati as the regional forwarding hub. ZYN and VELO mint variants lead the order mix from Imphal. We deliver across Imphal and the wider Manipur region under the standard pan-India shipping timeline, with delivery typically taking a day or two longer than central-India cities.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Imphal?",
//         a: "Yes — Imphal and the wider Manipur region are part of our pan-India delivery network. Standard delivery to Imphal typically lands in 3–5 business days.",
//       },
//       {
//         q: "Is Cash on Delivery available in Imphal?",
//         a: "Yes — Cash on Delivery is available on most Imphal orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Imphal buyers usually order?",
//         a: "Imphal's order mix leans mint-format. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Imphal buyers.",
//       },
//     ],
//   },

//   {
//     slug: "shillong",
//     name: "Shillong",
//     state: "Meghalaya",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Shillong, Meghalaya | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Shillong — Meghalaya's capital, the Scotland of the East. Authentic brands with pan-India shipping.",
//     intro:
//       "Shillong — Meghalaya's capital and the Scotland of the East — sits in the East Khasi Hills with a buyer profile shaped by tourism, education, and a steady local-service economy. The city's mix of locals, students, and tourists drives a small but consistent demand. ZYN and VELO mint variants lead the order mix from Shillong. We deliver across Shillong and the wider Meghalaya region under the standard pan-India shipping timeline, with Guwahati acting as the regional forwarding hub.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Shillong?",
//         a: "Yes — Shillong and the wider Meghalaya region are part of our pan-India delivery network. Delivery to Shillong typically lands in 3–5 business days.",
//       },
//       {
//         q: "Does the Shillong monsoon affect delivery times?",
//         a: "Meghalaya is among India's wettest regions. Heavy monsoon spells in June–September can add 1–2 days to delivery in Shillong. Standard delivery usually stays close to the regular window outside peak rains.",
//       },
//       {
//         q: "What do Shillong buyers usually order?",
//         a: "Shillong's order mix leans toward mint-format pouches. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Shillong buyers.",
//       },
//     ],
//   },

//   {
//     slug: "aizawl",
//     name: "Aizawl",
//     state: "Mizoram",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Aizawl, Mizoram | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Aizawl — Mizoram's capital. Authentic brands delivered with pan-India shipping. COD available.",
//     intro:
//       "Aizawl — Mizoram's capital and one of India's highest-altitude state capitals — has a buyer profile shaped by the state's literate, tightly-knit urban population. The city's administrative, education and small-trade base drives a modest but steady order flow, with ZYN and VELO mint variants leading the mix. We deliver across Aizawl and the wider Mizoram region under our pan-India shipping network, with delivery typically taking a day or two longer than the central-Indian cities.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Aizawl?",
//         a: "Yes — Aizawl and the wider Mizoram region are part of our pan-India delivery network. Delivery to Aizawl typically lands in 3–5 business days.",
//       },
//       {
//         q: "Is Cash on Delivery available in Aizawl?",
//         a: "Yes — Cash on Delivery is available on most Aizawl orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Aizawl buyers usually order?",
//         a: "Aizawl's order mix leans mint-format. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Aizawl buyers.",
//       },
//     ],
//   },

//   {
//     slug: "kohima",
//     name: "Kohima",
//     state: "Nagaland",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Kohima, Nagaland | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Kohima — Nagaland's capital. Authentic brands with pan-India shipping. Cash on Delivery available.",
//     intro:
//       "Kohima — Nagaland's capital and a city built across forested hills — has a buyer profile shaped by administrative, education and local-trade demographics. The city's small but growing service economy drives a modest but steady order flow. ZYN and VELO mint variants lead the order mix from Kohima. We deliver across Kohima and the wider Nagaland region under our pan-India shipping network, with Guwahati acting as the regional forwarding hub.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Kohima?",
//         a: "Yes — Kohima and the wider Nagaland region are part of our pan-India delivery network. Delivery to Kohima typically lands in 3–5 business days.",
//       },
//       {
//         q: "Is Cash on Delivery available in Kohima?",
//         a: "Yes — Cash on Delivery is available on most Kohima orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Kohima buyers usually order?",
//         a: "Kohima's order mix leans mint-format. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Kohima buyers.",
//       },
//     ],
//   },

//   {
//     slug: "gangtok",
//     name: "Gangtok",
//     state: "Sikkim",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Gangtok, Sikkim | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Gangtok — Sikkim's capital high in the Eastern Himalayas. Pan-India shipping with COD available.",
//     intro:
//       "Gangtok — Sikkim's capital perched in the Eastern Himalayas at over 5,400 feet — has a buyer profile shaped by tourism, administration and a small but steady urban population. The city's location as the gateway to upper Sikkim and the Indo-China border drives a varied seasonal demand. ZYN and VELO mint variants lead the order mix from Gangtok. We deliver across Gangtok and the wider Sikkim region under our pan-India shipping network, with delivery typically taking a day or two longer than the plain-area cities.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Gangtok?",
//         a: "Yes — Gangtok and the wider Sikkim region are part of our pan-India delivery network. Delivery to Gangtok typically lands in 3–5 business days.",
//       },
//       {
//         q: "Does the weather affect delivery to Gangtok?",
//         a: "Heavy rain (May–September) and occasional landslides on the mountain highways can add 1–2 days to delivery in Gangtok. Winter is usually unaffected unless there is heavy snow at higher altitudes.",
//       },
//       {
//         q: "What do Gangtok buyers usually order?",
//         a: "Gangtok's order mix leans mint-format. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Gangtok buyers.",
//       },
//     ],
//   },

//   {
//     slug: "agartala",
//     name: "Agartala",
//     state: "Tripura",
//     region: "Northeast India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Agartala, Tripura | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Agartala — Tripura's capital and a northeastern border city. Authentic brands with pan-India shipping.",
//     intro:
//       "Agartala — Tripura's capital and a border city on the edge of Bangladesh — has a buyer profile shaped by administrative, trade, and education demographics. The city's location as Tripura's primary urban centre drives a steady but modest order flow. ZYN and VELO mint variants lead the order mix from Agartala. We deliver across Agartala and the wider Tripura region under the standard pan-India shipping timeline, with Guwahati acting as the regional forwarding hub.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Agartala?",
//         a: "Yes — Agartala and the wider Tripura region are part of our pan-India delivery network. Delivery to Agartala typically lands in 3–5 business days.",
//       },
//       {
//         q: "Is Cash on Delivery available in Agartala?",
//         a: "Yes — Cash on Delivery is available on most Agartala orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Agartala buyers usually order?",
//         a: "Agartala's order mix leans mint-format. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Agartala buyers.",
//       },
//     ],
//   },

//   {
//     slug: "amaravati",
//     name: "Amaravati",
//     state: "Andhra Pradesh",
//     region: "South India",
//     isStateCapital: true,
//     seoTitle: "Buy Nicotine Pouches in Amaravati & AP Capital Region | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Amaravati and the AP capital region (Vijayawada/Guntur). Authentic brands with pan-India shipping.",
//     intro:
//       "Amaravati — Andhra Pradesh's planned capital on the banks of the Krishna river — is a relatively new urban centre, with most of the state's active commerce flowing through nearby Vijayawada and the coastal city of Visakhapatnam. We deliver across the Amaravati capital region (including Vijayawada and Guntur) and the wider Andhra Pradesh state under the standard pan-India shipping timeline. ZYN and VELO mint variants lead the order mix from the Amaravati region.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Amaravati and the AP capital region?",
//         a: "Yes — Amaravati, Vijayawada, Guntur and the wider Andhra Pradesh capital region are part of our standard pan-India delivery network. 2–4 business days standard delivery.",
//       },
//       {
//         q: "Is Cash on Delivery available in Amaravati?",
//         a: "Yes — Cash on Delivery is available on most Amaravati and Vijayawada-area orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Amaravati-area buyers usually order?",
//         a: "The Amaravati capital region's order mix is mint-leaning. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Amaravati and Vijayawada-area buyers.",
//       },
//     ],
//   },

//   {
//     slug: "nagpur",
//     name: "Nagpur",
//     state: "Maharashtra",
//     region: "Central India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Nagpur | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Nagpur — Maharashtra's third-largest city and the heart of India. Authentic brands with pan-India shipping.",
//     intro:
//       "Nagpur — Maharashtra's third-largest city and the geographic centre of India — sits at the crossroads of the country's north-south road and rail corridors. The city's mix of administrative (it serves as Maharashtra's winter capital), education, manufacturing and growing logistics drives a varied buyer profile. ZYN and VELO mint variants lead the order mix from Nagpur, with steady demand for slim-format mid-strength pouches. We deliver across Nagpur and the wider Vidarbha region under the standard pan-India shipping timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Nagpur?",
//         a: "Yes — Nagpur and the wider Vidarbha region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Nagpur pincodes.",
//       },
//       {
//         q: "Is Cash on Delivery available in Nagpur?",
//         a: "Yes — Cash on Delivery is available on most Nagpur orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Nagpur buyers usually order?",
//         a: "Nagpur's order mix leans mid-strength mint. ZYN Cool Mint, VELO Polar Mint Slim and ZYN Mighty Slim are the most consistent picks for Nagpur buyers.",
//       },
//     ],
//   },

//   {
//     slug: "visakhapatnam",
//     name: "Visakhapatnam",
//     state: "Andhra Pradesh",
//     region: "South India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Visakhapatnam (Vizag) | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Visakhapatnam (Vizag) — AP's largest city on the east coast. Authentic ZYN, VELO, Pablo with pan-India shipping.",
//     intro:
//       "Visakhapatnam — Andhra Pradesh's largest city and the Jewel of the East Coast — combines a major naval port, India's largest steel plant, and a growing IT and tourism base. The city's buyer profile is diverse, but slim-format mid-strength pouches lead the order mix from Vizag. We deliver across Visakhapatnam and onward through coastal Andhra Pradesh under the standard pan-India shipping timeline. Vizag also serves as our reliable forwarding hub for orders heading down the Andhra coast and into northern Andhra Pradesh.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Visakhapatnam?",
//         a: "Yes — Visakhapatnam (Vizag) and the wider coastal Andhra Pradesh region are part of our standard pan-India delivery network. 2–4 business days standard delivery.",
//       },
//       {
//         q: "Is Cash on Delivery available in Visakhapatnam?",
//         a: "Yes — Cash on Delivery is available on most Visakhapatnam orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Visakhapatnam buyers usually order?",
//         a: "Visakhapatnam's diverse buyer base shows steady mid-strength mint preference. VELO Polar Mint Slim, ZYN Cool Mint and Pablo Ice Cold are the most consistent picks here.",
//       },
//     ],
//   },

//   {
//     slug: "vadodara",
//     name: "Vadodara",
//     state: "Gujarat",
//     region: "West India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Vadodara, Gujarat | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Vadodara — Gujarat's cultural capital and petrochemicals hub. Authentic brands with pan-India shipping.",
//     intro:
//       "Vadodara — Gujarat's cultural capital and India's petrochemicals and engineering hub — has a buyer profile shaped by its mix of industrial, education, and Maharaja-heritage commerce. The city's IT and corporate professional base drives a steady order flow, with ZYN and VELO mint variants leading the mix from Vadodara. We deliver across Vadodara and onward through central Gujarat under the standard pan-India shipping timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Vadodara?",
//         a: "Yes — Vadodara and the wider central Gujarat region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Vadodara pincodes.",
//       },
//       {
//         q: "Is Cash on Delivery available in Vadodara?",
//         a: "Yes — Cash on Delivery is widely used in Vadodara and available on most orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Vadodara buyers usually order?",
//         a: "Vadodara's industrial and corporate buyer profile shows steady mint-format demand. ZYN Cool Mint Slim and VELO Polar Mint Slim are the most consistent picks here.",
//       },
//     ],
//   },

//   {
//     slug: "kanpur",
//     name: "Kanpur",
//     state: "Uttar Pradesh",
//     region: "North India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Kanpur, UP | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Kanpur — UP's largest commercial city and the Leather City of the World. Authentic brands with pan-India shipping.",
//     intro:
//       "Kanpur — Uttar Pradesh's largest commercial city and the Leather City of the World — has a buyer profile shaped by its industrial, education, and trading base. The city's mix of IIT Kanpur's academic crowd, the leather and chemical industries, and the dense commercial centre drives a steady order flow. ZYN and VELO mint variants lead the order mix from Kanpur. We deliver across Kanpur and the wider central UP region under the standard pan-India shipping timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Kanpur?",
//         a: "Yes — Kanpur and the wider Uttar Pradesh region are part of our standard pan-India delivery network. Standard delivery to Kanpur pincodes lands in 2–4 business days.",
//       },
//       {
//         q: "Is Cash on Delivery available in Kanpur?",
//         a: "Yes — Cash on Delivery is available on most Kanpur orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Kanpur buyers usually order?",
//         a: "Kanpur's order mix leans mid-strength mint. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Kanpur buyers.",
//       },
//     ],
//   },

//   {
//     slug: "agra",
//     name: "Agra",
//     state: "Uttar Pradesh",
//     region: "North India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Agra, UP | Fast Delivery | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Agra — the city of the Taj Mahal. Authentic ZYN, VELO, Pablo with pan-India shipping. COD available.",
//     intro:
//       "Agra — the city of the Taj Mahal and the Mughal heritage capital of India — has a buyer profile shaped by its tourism economy, leather and marble industries, and a growing service base. The city's mix of resident commerce and tourism-related demand drives a steady order flow. ZYN and VELO mint variants lead the order mix from Agra. We deliver across Agra and the wider western UP region under the standard pan-India shipping timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Agra?",
//         a: "Yes — Agra and the wider western Uttar Pradesh region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Agra pincodes.",
//       },
//       {
//         q: "Is Cash on Delivery available in Agra?",
//         a: "Yes — Cash on Delivery is available on most Agra orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Agra buyers usually order?",
//         a: "Agra's order mix leans mint-format. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Agra buyers.",
//       },
//     ],
//   },

//   {
//     slug: "varanasi",
//     name: "Varanasi",
//     state: "Uttar Pradesh",
//     region: "North India",
//     isStateCapital: false,
//     seoTitle: "Buy Nicotine Pouches in Varanasi, UP | ZeroSmoke",
//     seoDescription:
//       "Buy nicotine pouches in Varanasi — one of the world's oldest cities. Authentic brands with pan-India shipping. Cash on Delivery available.",
//     intro:
//       "Varanasi — one of the world's oldest continuously inhabited cities and India's spiritual capital — combines ancient Ganga-side heritage with a growing tier-2 commercial base. The city's buyer profile is shaped by tourism, education (BHU), silk weaving, and a steady local commerce. ZYN and VELO mint variants lead the order mix from Varanasi. We deliver across Varanasi and onward through eastern UP under the standard pan-India shipping timeline.",
//     faqs: [
//       {
//         q: "Do you deliver nicotine pouches to Varanasi?",
//         a: "Yes — Varanasi and the wider eastern Uttar Pradesh region are part of our standard pan-India delivery network. 2–4 business days standard delivery to Varanasi pincodes.",
//       },
//       {
//         q: "Is Cash on Delivery available in Varanasi?",
//         a: "Yes — Cash on Delivery is available on most Varanasi orders. Free shipping kicks in on orders over ₹5,000.",
//       },
//       {
//         q: "What do Varanasi buyers usually order?",
//         a: "Varanasi's order mix leans mid-strength mint. ZYN Cool Mint and VELO Polar Mint Slim are the most consistent picks for Varanasi buyers.",
//       },
//     ],
//   },
// ];

export const cityBySlug: Record<string, CityInfo> = Object.fromEntries(
  cities.map((c) => [c.slug, c])
);

/** Cities grouped by region — used by the /city index page for the
 *  region-bucketed display. Order within each region is preserved from
 *  the `cities` array above (insertion order). */
export function citiesByRegion(): Record<CityInfo["region"], CityInfo[]> {
  const out: Record<string, CityInfo[]> = {};
  for (const c of cities) {
    (out[c.region] ??= []).push(c);
  }
  return out as Record<CityInfo["region"], CityInfo[]>;
}
